import React, { useEffect, useMemo, useState } from "react";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Button from "@amzn/meridian/button";
import Select, { SelectOption } from "@amzn/meridian/select";
import Input from "@amzn/meridian/input";
import Box from "@amzn/meridian/box";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";

import { CertificateFunctionMapping, Connector, ExpressionNode, MappableFunction } from "./types";
import { chainToTree, parseExpression, renderExpression, treeToChain, validateChain } from "./transform";

interface CertificateRuleModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (processId: string, functionId: string, expression: ExpressionNode, version?: number) => void;
  /** Mapping being edited, or null when adding */
  editMapping: CertificateFunctionMapping | null;
  availableFunctions: MappableFunction[];
  /** processId|functionId keys that already have a mapping (hidden in Add mode) */
  mappedKeys: Set<string>;
  isSaving: boolean;
  /** Server-side error passed from the parent */
  serverError?: string;
}

export const functionKey = (f: { processId: string; functionId: string }): string =>
  `${f.processId}|${f.functionId}`;

/** How a function's ids are shown to users, e.g. "1003058 - 4300006654". */
export const functionDisplayKey = (f: { processId: string; functionId: string }): string =>
  `${f.processId} - ${f.functionId}`;

export const functionLabel = (f: { processId: string; functionId: string; functionName?: string }): string =>
  `${f.functionName || "Unknown function"} — ${functionDisplayKey(f)}`;

const CertificateRuleModal: React.FC<CertificateRuleModalProps> = ({
  open,
  onClose,
  onSave,
  editMapping,
  availableFunctions,
  mappedKeys,
  isSaving,
  serverError,
}) => {
  const [selectedKey, setSelectedKey] = useState("");
  const [certs, setCerts] = useState<string[]>([]);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [certInput, setCertInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isEditMode = editMapping !== null;

  // Reset form state when modal opens or the edited mapping changes
  useEffect(() => {
    if (!open) return;
    setError(null);
    setCertInput("");
    if (editMapping) {
      setSelectedKey(functionKey(editMapping));
      const tree = parseExpression(editMapping.expression);
      const chain = tree ? treeToChain(tree) : { certs: [], connectors: [] };
      setCerts(chain.certs);
      setConnectors(chain.connectors);
    } else {
      setSelectedKey("");
      setCerts([]);
      setConnectors([]);
    }
  }, [open, editMapping]);

  const selectableFunctions = useMemo(
    () =>
      availableFunctions
        .filter((f) => !mappedKeys.has(functionKey(f)))
        .sort(
          (a, b) =>
            a.processPath.localeCompare(b.processPath) || a.functionName.localeCompare(b.functionName)
        ),
    [availableFunctions, mappedKeys]
  );

  const chainError = validateChain(certs, connectors);
  const preview = certs.length && !chainError ? renderExpression(chainToTree(certs, connectors)) : null;

  const handleAddCert = () => {
    const value = certInput.trim();
    if (!value) return;
    if (certs.length > 0) setConnectors((prev) => [...prev, null]);
    setCerts((prev) => [...prev, value]);
    setCertInput("");
    setError(null);
  };

  // Removing a certificate also removes its adjacent connector (same as the mock)
  const handleRemoveCert = (index: number) => {
    setCerts((prev) => prev.filter((_, i) => i !== index));
    setConnectors((prev) => {
      const next = [...prev];
      if (index < next.length) next.splice(index, 1);
      else next.pop();
      return next;
    });
    setError(null);
  };

  const handleSetConnector = (index: number, value: Connector) => {
    setConnectors((prev) => prev.map((c, i) => (i === index ? value : c)));
    setError(null);
  };

  const handleSubmit = () => {
    if (!selectedKey) {
      setError("Select a Function ID");
      return;
    }
    if (chainError) {
      setError(chainError);
      return;
    }
    const [processId, functionId] = selectedKey.split("|");
    onSave(processId, functionId, chainToTree(certs, connectors), editMapping?.version);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditMode ? "Edit Mapping" : "Add Mapping"}
      scrollContainer="viewport"
      closeLabel="Close"
      width="580px"
    >
      <Box spacingInset="400">
        {(error || serverError) && (
          <Box spacingInset="none none 400 none">
            <Alert type="error">{error || serverError}</Alert>
          </Box>
        )}

        <Column spacing="400">
          {/* Function */}
          <Box>
            <Text type="b200">Function ID</Text>
            {isEditMode && editMapping ? (
              <Text type="b300">{functionLabel(editMapping)}</Text>
            ) : (
              <Select
                value={selectedKey}
                onChange={(value: string) => {
                  setSelectedKey(value);
                  setError(null);
                }}
                placeholder="Select a function…"
                width="100%"
              >
                {selectableFunctions.map((f) => (
                  <SelectOption key={functionKey(f)} value={functionKey(f)} label={functionLabel(f)} />
                ))}
              </Select>
            )}
          </Box>

          {/* Certificate builder */}
          <Box>
            <Text type="b200">Certificate IDs</Text>
            <Column spacing="200">
              {certs.map((cert, i) => (
                <React.Fragment key={`${cert}-${i}`}>
                  <Row spacing="200" alignmentVertical="center" widths={["fill", "fit"]}>
                    <Text type="b300">{cert}</Text>
                    <Button type="icon" size="small" onClick={() => handleRemoveCert(i)} label={`Remove ${cert}`}>
                      ×
                    </Button>
                  </Row>
                  {i < certs.length - 1 && (
                    <Row spacing="100">
                      <Button
                        type={connectors[i] === "AND" ? "primary" : "tertiary"}
                        size="small"
                        onClick={() => handleSetConnector(i, "AND")}
                      >
                        AND
                      </Button>
                      <Button
                        type={connectors[i] === "OR" ? "primary" : "tertiary"}
                        size="small"
                        onClick={() => handleSetConnector(i, "OR")}
                      >
                        OR
                      </Button>
                    </Row>
                  )}
                </React.Fragment>
              ))}
            </Column>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAddCert();
              }}
            >
              <Row spacing="200" alignmentVertical="bottom" widths={["fill", "fit"]}>
                <Input
                  value={certInput}
                  onChange={(v: string) => setCertInput(v)}
                  placeholder="Type certificate ID and press Add…"
                  size="small"
                />
                <Button type="secondary" size="small" onClick={handleAddCert}>
                  Add
                </Button>
              </Row>
            </form>
            <Text type="b100" color="secondary">
              Between each certificate, select AND or OR to define eligibility logic. AND is applied before OR.
            </Text>
          </Box>

          {preview && (
            <Box>
              <Text type="b200">Rule: {preview}</Text>
            </Box>
          )}
        </Column>
      </Box>

      <ModalFooter>
        <Row alignmentHorizontal="end" spacing="300">
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Mapping"}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default CertificateRuleModal;
